"use client";

import MediaImage from "@/components/ui/MediaImage";

import React, { useEffect, useRef, useState, useId } from "react";
import type {
  Article,
  ArticleBlock,
  ArticleComponentConfig,
  ArticleContext,
  ArticleImage,
  BlogAuthor,
  BlogData,
} from "@/blog/models";
import { articleVariations } from "@/blog/models";
import { articleImageTextStyle } from "@/blog/image-text-style";
import { articleHeaderStyle, articleLayoutStyle } from "@/blog/layout-style";
import {
  articleUrl,
  articleDetailsSlug,
  safeArticleLink,
  selectArticles,
  normalizeArticleVideoUrl,
} from "@/blog/article-service";
import {
  articleBlog,
  articleEyebrow,
  articleExcerpt,
  articleReadingMinutes,
  authorPath,
  blogPath,
  mainTopic,
  newStyleArticlePath,
  newStyleBlogs,
  relatedArticles,
  topicPath,
  withVisibleBlocks,
} from "@/blog/article-paths";
import { useArticleFilter } from "./ArticleInteractionProvider";
import { ArticleRichText } from "./ArticleRichText";
import ArticleDate from "./ArticleDate";

function Photo({
  image,
  priority = false,
  fallbackPosition,
}: {
  image?: ArticleImage;
  priority?: boolean;
  fallbackPosition?: string;
}) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [image?.url]);
  return image && !failed && safeArticleLink(image.url) ? (
    <MediaImage
      onError={() => setFailed(true)}
      src={image.url}
      alt={image.altText ?? image.caption ?? "Artikelbillede"}
      width={image.width}
      height={image.height}
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      style={
        {
          objectPosition: image.position ?? fallbackPosition ?? "center",
          "--a-image-width": image.width ? `${image.width}px` : undefined,
          "--a-image-ratio":
            image.width && image.height
              ? `${image.width} / ${image.height}`
              : undefined,
        } as React.CSSProperties
      }
    />
  ) : (
    <div className="a-no-image" aria-label="Artikel uden billede">
      <span aria-hidden="true">↗</span>
    </div>
  );
}
function Metadata({
  post,
  authors,
  variant = "inline",
  locale,
  readingLabel = "min. læsning",
  showDate = true,
  showAuthor,
  authorHref,
}: {
  post: Article;
  authors: BlogAuthor[];
  variant?: string;
  locale?: string;
  readingLabel?: string;
  showDate?: boolean;
  showAuthor?: boolean;
  authorHref?: (author: BlogAuthor) => string | undefined;
}) {
  const named = authors.filter((author) => !!author.name?.trim());
  const names = named.map((author) => author.name.trim());
  const byline = (showAuthor ?? variant === "author") && names.length > 0;
  return (
    <div className={`a-meta a-meta-${variant}`}>
      {byline && (
        <span className="a-avatar" aria-hidden="true">
          {names[0].slice(0, 1)}
        </span>
      )}
      <div>
        {byline && (
          <strong>
            {authorHref
              ? named.map((author, index) => {
                  const href = authorHref(author);
                  return (
                    <React.Fragment key={author.id}>
                      {index > 0 && ", "}
                      {href ? <a href={href}>{names[index]}</a> : names[index]}
                    </React.Fragment>
                  );
                })
              : names.join(", ")}
          </strong>
        )}
        {showDate && <ArticleDate value={post.publishedAt} locale={locale} />}
        {showDate && variant !== "minimal" && (
          <span>
            {" "}
            {"·"} {articleReadingMinutes(post)} {readingLabel}
          </span>
        )}
      </div>
    </div>
  );
}
/** How an article of a new-style artikelside is presented: its own address, main topic and excerpt. */
interface NewStylePresentation {
  href: string;
  eyebrow: string;
  excerpt: string;
}
function newStylePresentation(
  blog: BlogData | undefined,
  post: Article,
): NewStylePresentation | undefined {
  const href = newStyleArticlePath(blog, post);
  return href
    ? {
        href,
        eyebrow: articleEyebrow(blog, post),
        excerpt: articleExcerpt(post),
      }
    : undefined;
}
const hasImage = (image?: ArticleImage) =>
  !!image?.url && !!safeArticleLink(image.url);
function Card({
  post,
  config,
  variant = "classic",
  authors = [],
  newStyle,
}: {
  post: Article;
  config: ArticleComponentConfig;
  variant?: string;
  authors?: BlogAuthor[];
  newStyle?: NewStylePresentation;
}) {
  const href = newStyle
    ? newStyle.href
    : config.detailsPageSlug
      ? articleUrl(config.detailsPageSlug, post.slug)
      : undefined;
  const image = newStyle ? post.cover : (post.thumbnail ?? post.cover);
  const textOnly = !!newStyle && !hasImage(image);
  const contents = (
    <>
      {!textOnly && (
        <div className="a-card-media">
          <Photo image={image} fallbackPosition={config.imagePosition} />
        </div>
      )}
      <div className="a-card-copy">
        {newStyle ? (
          newStyle.eyebrow && <p className="a-eyebrow">{newStyle.eyebrow}</p>
        ) : (
          <p className="a-eyebrow">{post.category}</p>
        )}
        <h3 className="primary-font">{post.title}</h3>
        {config.showExcerpt !== false &&
          (newStyle ? (
            newStyle.excerpt && <p className="a-excerpt">{newStyle.excerpt}</p>
          ) : (
            <p className="a-excerpt">{post.excerpt}</p>
          ))}
        {config.showMetadata !== false && (
          <Metadata
            post={post}
            authors={authors}
            locale={config.dateLocale}
            readingLabel={config.readingLabel}
            showDate={config.showDate}
            showAuthor={config.showAuthor}
          />
        )}
        <span className="a-read">
          {config.linkLabel || (newStyle ? "Læs artiklen" : "Læs fortællingen")}{" "}
          <span aria-hidden="true">
            {config.linkIcon === "chevron" ? "›" : newStyle ? "→" : "↗"}
          </span>
        </span>
      </div>
    </>
  );
  return (
    <article
      className={`a-card a-card-${variant}${textOnly ? " a-card-text" : ""}`}
    >
      {href ? <a href={href}>{contents}</a> : <div>{contents}</div>}
    </article>
  );
}
function Media({
  images,
  variant,
  videoUrl,
  caption,
}: {
  images: ArticleImage[];
  variant: string;
  videoUrl?: string;
  caption?: string;
}) {
  const safe = normalizeArticleVideoUrl(videoUrl);
  const embed =
    safe?.includes("youtube.com/embed/") ||
    safe?.includes("player.vimeo.com/video/")
      ? safe
      : undefined;
  return (
    <figure className={`a-media a-media-${variant}`}>
      {variant === "video" && safe && /\.(mp4|webm)(\?|$)/i.test(safe) ? (
        <video controls preload="none" poster={images[0]?.url}>
          <source src={safe} />
        </video>
      ) : variant === "video" && embed ? (
        <iframe
          src={embed}
          title={caption ?? "Artikelvideo"}
          loading="lazy"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      ) : (
        <div className="a-media-images">
          {(variant === "gallery" ? images : images.slice(0, 1)).map(
            (image, i) =>
              variant === "gallery" ? (
                <figure key={`${image.url}-${i}`}>
                  <Photo image={image} />
                  {image.caption && <figcaption>{image.caption}</figcaption>}
                </figure>
              ) : (
                <Photo key={`${image.url}-${i}`} image={image} />
              ),
          )}
        </div>
      )}
      {(caption || (variant !== "gallery" && images[0]?.caption)) && (
        <figcaption>{caption ?? images[0]?.caption}</figcaption>
      )}
    </figure>
  );
}
function Block({
  block,
  prefix,
  newStyle = false,
}: {
  block: ArticleBlock;
  prefix: string;
  /** An article of an artikelside with a path: a Faktaboks without a title has no heading, and only external buttons get »↗«. */
  newStyle?: boolean;
}) {
  switch (block.type) {
    case "richText":
      return (
        <ArticleRichText html={block.html} idPrefix={`${prefix}-${block.id}`} />
      );
    case "image":
      return <Media images={[block.image]} variant="wide" />;
    case "imageText": {
      const side = block.imageSide === "right" ? "right" : "left";
      const copyFirst =
        block.mobileOrder === "text-first" ||
        (block.mobileOrder === "follow-side" && side === "right");
      return (
        <div
          className="a-image-text"
          data-image-side={side}
          data-mobile-order={copyFirst ? "text-first" : "image-first"}
          data-stack-breakpoint={block.stackBreakpoint === 992 ? 992 : 768}
          style={articleImageTextStyle(block)}
        >
          <div className="a-image-text-media">
            <figure>
              <Photo image={block.image} />
            </figure>
          </div>
          <div className="a-image-text-copy">
            <div className="a-image-text-inner">
              <ArticleRichText
                html={block.html}
                idPrefix={`${prefix}-${block.id}`}
                extendedHeadings
              />
            </div>
          </div>
        </div>
      );
    }
    case "gallery":
      return <Media images={block.images} variant="gallery" />;
    case "video":
      return (
        <Media
          images={block.poster ? [block.poster] : []}
          variant="video"
          videoUrl={block.url}
          caption={block.caption}
        />
      );
    case "quote":
      return (
        <blockquote>
          <p>{block.text}</p>
          {block.attribution && <cite>{block.attribution}</cite>}
        </blockquote>
      );
    case "callout":
      return (
        <aside className="a-callout">
          {(!newStyle || !!block.title?.trim()) && (
            <h3 className="primary-font">{block.title}</h3>
          )}
          <p>{block.text}</p>
        </aside>
      );
    case "button":
      return safeArticleLink(block.url) ? (
        <a className="a-button" href={block.url}>
          {block.label}
          {(!newStyle || /^https?:\/\//i.test(block.url.trim())) && " ↗"}
        </a>
      ) : null;
  }
}
function State({ variant, reset }: { variant: string; reset?: () => void }) {
  if (variant === "loading")
    return (
      <div className="a-skeletons" role="status" aria-label="Henter artikler">
        {[1, 2, 3].map((id) => (
          <div key={id} className="a-skeleton" aria-hidden="true">
            <div />
            <span />
            <span />
          </div>
        ))}
      </div>
    );
  const message =
    variant === "empty"
      ? [
          "Journalen begynder snart.",
          "Vi glæder os til at dele nye fortællinger med dig.",
        ]
      : variant === "error"
        ? ["Fortællingerne er lige på vej.", "Prøv igen om et øjeblik."]
        : [
            "Ingen fortællinger fundet.",
            "Prøv et andet søgeord, eller gå på opdagelse i alle artikler.",
          ];
  return (
    <div className="a-state" role="status">
      <p className="a-eyebrow">JOURNAL</p>
      <h2 className="primary-font">{message[0]}</h2>
      <p>{message[1]}</p>
      {variant === "no-results" && reset && (
        <button className="a-button" onClick={reset}>
          Vis alle artikler
        </button>
      )}
      {variant === "error" && (
        <button className="a-button" onClick={() => window.location.reload()}>
          Prøv igen
        </button>
      )}
    </div>
  );
}

export default function ArticleView({
  config,
  blog: data,
  articleContext: dataContext,
  form,
}: {
  config: ArticleComponentConfig;
  blog?: BlogData;
  articleContext?: ArticleContext;
  form?: React.ReactNode;
}) {
  const blog = data && {
    ...data,
    posts: (data.posts ?? []).map((post) => withVisibleBlocks(data, post)),
  };
  const articleContext = dataContext && {
    ...dataContext,
    article: withVisibleBlocks(data, dataContext.article),
  };
  const instanceId = useId().replaceAll(":", "");
  const family = config.family;
  const variations: readonly string[] = articleVariations[family] ?? [];
  const variant = variations.includes(config.variant ?? "")
    ? config.variant!
    : variations[0];
  const { filter, update, listing } = useArticleFilter(
    config.listingKey ?? config.blogId ?? "articles",
  );
  const source =
    (family === "pagination" || family === "discovery") && listing
      ? {
          ...config,
          ...listing,
          family: config.family,
          variant: config.variant,
        }
      : config;
  const settings = {
    ...source,
    detailsPageSlug:
      config.detailsPageSlug ??
      blog?.blogs.find((item) => item.id === config.blogId)?.detailsPageSlug ??
      articleContext?.detailsPageSlug,
  };
  const carousel = useRef<HTMLDivElement>(null);
  const sentinel = useRef<HTMLDivElement>(null);
  /** A legacy list on a website that also has artikelsider with a path draws the legacy articles it drew before. */
  const legacyPool =
    !config.newStyle &&
    !articleContext?.newStyle &&
    !settings.blogId &&
    !settings.articleIds?.length &&
    !settings.articleId &&
    newStyleBlogs(blog).length > 0;
  const pool = legacyPool
    ? (blog?.posts ?? []).filter((item) => !newStyleArticlePath(blog, item))
    : (blog?.posts ?? []);
  const all =
    family === "related" && articleContext?.newStyle
      ? relatedArticles(pool, articleContext.article, config.limit ?? 3)
      : selectArticles(pool, settings, articleContext?.article);
  const posts = all.filter(
    (post) =>
      (!filter.category || post.category === filter.category) &&
      (!filter.query ||
        `${post.title} ${post.excerpt} ${(post.tags ?? []).join(" ")}`
          .toLocaleLowerCase("da-DK")
          .includes(filter.query.toLocaleLowerCase("da-DK"))),
  );
  const post =
    (config.newStyle ? undefined : articleContext?.article) ??
    all.find((item) => item.id === config.articleId) ??
    (config.articleId ? undefined : all[0]);
  const postStyle = post ? newStylePresentation(blog, post) : undefined;
  const postBlog = postStyle && post ? articleBlog(blog, post) : undefined;
  const overviewPath =
    articleContext?.newStyle && articleContext.backPath !== "/"
      ? blogPath(postBlog)
      : undefined;
  const topicHref =
    overviewPath && post && postBlog?.topicPages !== false
      ? (() => {
          const topic = mainTopic(blog, post);
          return topic ? topicPath(overviewPath, topic) : undefined;
        })()
      : undefined;
  const authorHref =
    overviewPath && postBlog?.authorPages === true
      ? (author: BlogAuthor) => authorPath(overviewPath, author)
      : undefined;
  const detailsSlug = post
    ? articleDetailsSlug(blog, config, post, articleContext?.detailsPageSlug)
    : undefined;
  const authors = (blog?.authors ?? []).filter((author) =>
    post?.authorIds?.includes(author.id),
  );
  const pageSize = Math.max(1, Math.min(100, Math.round(source.pageSize ?? 6)));
  const pagination = config.pagination ?? "numbers";
  const page = Math.min(
    filter.page,
    Math.max(1, Math.ceil(posts.length / pageSize)),
  );
  const visible = Math.max(pageSize, filter.visible);
  const shown =
    family === "collection"
      ? pagination === "none"
        ? posts
        : pagination === "numbers"
          ? posts.slice((page - 1) * pageSize, page * pageSize)
          : posts.slice(0, visible)
      : posts;
  useEffect(() => {
    if (
      family !== "collection" ||
      pagination !== "infinite" ||
      !sentinel.current ||
      visible >= posts.length
    )
      return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting))
          update({ visible: visible + pageSize });
      },
      { rootMargin: "100px" },
    );
    observer.observe(sentinel.current);
    return () => observer.disconnect();
  }, [family, pagination, visible, posts.length, pageSize, update]);
  const reset = () =>
    update({ category: "", query: "", page: 1, visible: pageSize });
  const newRelated = family === "related" && !!articleContext?.newStyle;
  const heading = (
    <div className="a-heading">
      {newRelated ? (
        config.eyebrow && <p className="a-eyebrow">{config.eyebrow}</p>
      ) : (
        <p className="a-eyebrow">
          {config.eyebrow ??
            (family === "related" ? "LÆS VIDERE" : "FRA VORES JOURNAL")}
        </p>
      )}
      <h2 className="primary-font">
        {config.title ??
          (newRelated
            ? "Læs også"
            : family === "related"
              ? "Der er mere at opdage."
              : "Nye fortællinger.")}
      </h2>
      {config.text && <p>{config.text}</p>}
    </div>
  );
  const card = (
    article: Article,
    cardVariant = config.cardVariant ?? "classic",
  ) => (
    <Card
      key={article.id}
      post={article}
      config={{
        ...settings,
        detailsPageSlug: articleDetailsSlug(
          blog,
          config,
          article,
          articleContext?.detailsPageSlug,
        ),
      }}
      variant={cardVariant}
      authors={(blog?.authors ?? []).filter((author) =>
        article.authorIds?.includes(author.id),
      )}
      newStyle={newStylePresentation(blog, article)}
    />
  );
  const paginate = (kind: string) =>
    kind === "numbers" ? (
      <nav className="a-pagination" aria-label="Artikelsider">
        <button
          aria-label="Forrige side"
          disabled={page <= 1}
          onClick={() => update({ page: page - 1 })}
        >
          ←
        </button>
        {Array.from(
          { length: Math.ceil(posts.length / pageSize) },
          (_, i) => i + 1,
        )
          .filter(
            (number) =>
              number === 1 ||
              number === Math.ceil(posts.length / pageSize) ||
              Math.abs(number - page) < 3,
          )
          .map((number) => (
            <button
              key={number}
              aria-current={page === number ? "page" : undefined}
              onClick={() => update({ page: number })}
            >
              {number}
            </button>
          ))}
        <button
          aria-label="Næste side"
          disabled={page >= Math.ceil(posts.length / pageSize)}
          onClick={() => update({ page: page + 1 })}
        >
          →
        </button>
      </nav>
    ) : (
      <div className="a-load-more">
        <p>
          {Math.min(visible, posts.length)} af {posts.length} artikler
        </p>
        <progress
          value={Math.min(visible, posts.length)}
          max={Math.max(1, posts.length)}
          aria-label="Viste artikler"
        />
        {visible < posts.length ? (
          <button
            className="a-button a-outline"
            onClick={() => update({ visible: visible + pageSize })}
          >
            Vis flere artikler ↓
          </button>
        ) : (
          <p>Du har set alle artikler.</p>
        )}
      </div>
    );
  let content: React.ReactNode;
  switch (family) {
    case "intro":
      content = (
        <div className={`a-intro a-intro-${variant}`}>
          {variant === "banner" && config.image && (
            <Photo image={config.image} priority />
          )}
          <div>
            <p className="a-eyebrow">
              {config.eyebrow ?? "INSPIRATION & INDSIGT"}
            </p>
            <h1 className="primary-font">
              {config.title ?? "Hjem. Mennesker. Nye kapitler."}
            </h1>
            <p>
              {config.text ??
                "Fortællinger, inspiration og gode råd til dit næste kapitel."}
            </p>
          </div>
        </div>
      );
      break;
    case "featured":
      content = !post ? (
        <State variant="empty" />
      ) : variant === "magazine" ? (
        <div className="a-magazine-feature">
          <div>{card(post, "cover")}</div>
          <div className="a-companions">
            {all
              .filter((article) => article.id !== post.id)
              .slice(0, 2)
              .map((article) => card(article, "horizontal"))}
          </div>
        </div>
      ) : variant === "cover" ? (
        card(post, "cover")
      ) : postStyle ? (
        <div
          className={
            hasImage(post.cover) ? "a-feature-split" : "a-feature-text"
          }
        >
          {hasImage(post.cover) && (
            <div>
              <Photo
                image={post.cover}
                priority
                fallbackPosition={config.imagePosition}
              />
            </div>
          )}
          <div>
            {(config.eyebrow || postStyle.eyebrow) && (
              <p className="a-eyebrow">
                {[config.eyebrow, postStyle.eyebrow]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            )}
            <h2 className="primary-font">{post.title}</h2>
            {config.metadataPlacement === "before-excerpt" &&
              config.showMetadata !== false && (
                <Metadata
                  post={post}
                  authors={authors}
                  locale={config.dateLocale}
                  readingLabel={config.readingLabel}
                  showDate={config.showDate}
                  showAuthor={config.showAuthor}
                />
              )}
            {config.showExcerpt !== false && postStyle.excerpt && (
              <p>{postStyle.excerpt}</p>
            )}
            {config.metadataPlacement !== "before-excerpt" &&
              config.showMetadata !== false && (
                <Metadata
                  post={post}
                  authors={authors}
                  locale={config.dateLocale}
                  readingLabel={config.readingLabel}
                  showDate={config.showDate}
                  showAuthor={config.showAuthor}
                />
              )}
            <a className="a-read" href={postStyle.href}>
              {config.linkLabel || "Læs artiklen"}{" "}
              <span aria-hidden="true">
                {config.linkIcon === "chevron" ? "›" : "→"}
              </span>
            </a>
          </div>
        </div>
      ) : (
        <div className="a-feature-split">
          <div>
            <Photo
              image={post.cover}
              priority
              fallbackPosition={config.imagePosition}
            />
          </div>
          <div>
            <p className="a-eyebrow">
              {config.eyebrow ?? "UDVALGT FORTÆLLING"} · {post.category}
            </p>
            <h2 className="primary-font">{post.title}</h2>
            {config.metadataPlacement === "before-excerpt" &&
              config.showMetadata !== false && (
                <Metadata
                  post={post}
                  authors={authors}
                  locale={config.dateLocale}
                  readingLabel={config.readingLabel}
                  showDate={config.showDate}
                  showAuthor={config.showAuthor}
                />
              )}
            {config.showExcerpt !== false && <p>{post.excerpt}</p>}
            {config.metadataPlacement !== "before-excerpt" &&
              config.showMetadata !== false && (
                <Metadata
                  post={post}
                  authors={authors}
                  locale={config.dateLocale}
                  readingLabel={config.readingLabel}
                  showDate={config.showDate}
                  showAuthor={config.showAuthor}
                />
              )}
            <a
              className="a-read"
              href={
                detailsSlug ? articleUrl(detailsSlug, post.slug) : undefined
              }
            >
              {config.linkLabel || "Læs fortællingen"}{" "}
              <span aria-hidden="true">
                {config.linkIcon === "chevron" ? "›" : "↗"}
              </span>
            </a>
          </div>
        </div>
      );
      break;
    case "collection":
      content = (
        <>
          {(config.title || config.eyebrow || config.text) && heading}
          {!config.newStyle && (
            <div className="a-results" aria-live="polite">
              {posts.length} {posts.length === 1 ? "artikel" : "artikler"}
            </div>
          )}
          {posts.length ? (
            <div
              className={`a-collection a-collection-${variant}`}
              style={
                {
                  "--article-cols-mobile": Math.max(
                    1,
                    Math.min(2, config.columns?.mobile ?? 1),
                  ),
                  "--article-cols-desktop": Math.max(
                    1,
                    Math.min(4, config.columns?.desktop ?? 3),
                  ),
                } as React.CSSProperties
              }
            >
              {shown.map((article, i) =>
                card(
                  article,
                  variant === "list"
                    ? "horizontal"
                    : variant === "magazine" && i === 0
                      ? "cover"
                      : config.cardVariant,
                ),
              )}
            </div>
          ) : (
            <State
              variant={all.length ? "no-results" : "empty"}
              reset={reset}
            />
          )}
          {config.showPagination !== false &&
            pagination !== "none" &&
            posts.length > pageSize &&
            paginate(pagination)}
          {pagination === "infinite" && (
            <div ref={sentinel} className="a-sentinel" aria-hidden="true" />
          )}
        </>
      );
      break;
    case "home":
    case "related":
      if (family === "related" && articleContext?.newStyle && !all.length)
        return null;
      content = (
        <>
          {heading}
          {variant === "carousel" && (
            <div className="a-carousel-controls">
              <button
                aria-label="Forrige artikler"
                onClick={() =>
                  carousel.current?.scrollBy({
                    left: -carousel.current.clientWidth,
                    behavior: "smooth",
                  })
                }
              >
                ←
              </button>
              <button
                aria-label="Næste artikler"
                onClick={() =>
                  carousel.current?.scrollBy({
                    left: carousel.current.clientWidth,
                    behavior: "smooth",
                  })
                }
              >
                →
              </button>
            </div>
          )}
          <div
            ref={carousel}
            className={`a-highlights a-highlights-${variant}`}
          >
            {all
              .slice(0, config.limit ?? (variant === "carousel" ? 6 : 3))
              .map((article, index) =>
                card(
                  article,
                  variant === "list" ||
                    (variant === "feature-list" && index > 0)
                    ? "horizontal"
                    : config.cardVariant,
                ),
              )}
          </div>
          {!all.length && <State variant="empty" />}
          {safeArticleLink(config.link) && (
            <a className="a-read" href={config.link}>
              {config.linkLabel ?? "Alle fortællinger"} ↗
            </a>
          )}
        </>
      );
      break;
    case "card":
      content = post
        ? card(post, variant as "classic" | "horizontal" | "cover")
        : null;
      break;
    case "discovery": {
      const categories = [
        ...new Set(all.map((article) => article.category).filter(Boolean)),
      ] as string[];
      content = (
        <div className={`a-discovery a-discovery-${variant}`}>
          {variant === "dropdown" ? (
            <label>
              Emne
              <select
                value={filter.category}
                onChange={(event) =>
                  update({
                    category: event.target.value,
                    page: 1,
                    visible: pageSize,
                  })
                }
              >
                <option value="">Alle emner</option>
                {categories.map((category) => (
                  <option key={category}>{category}</option>
                ))}
              </select>
            </label>
          ) : (
            <div
              className="a-categories"
              role="group"
              aria-label="Filtrér efter emne"
            >
              {["", ...categories].map((category) => (
                <button
                  key={category}
                  aria-pressed={category === filter.category}
                  onClick={() =>
                    update({ category, page: 1, visible: pageSize })
                  }
                >
                  {category || "Alle emner"}
                </button>
              ))}
            </div>
          )}
          <label className="a-search">
            <span className="a-sr-only">Søg i artikler</span>
            <input
              type="search"
              placeholder="Søg i artikler"
              value={filter.query}
              onChange={(event) =>
                update({
                  query: event.target.value,
                  page: 1,
                  visible: pageSize,
                })
              }
            />
          </label>
        </div>
      );
      break;
    }
    case "pagination":
      content = posts.length ? paginate(variant) : null;
      break;
    case "post-header": {
      if ((!articleContext && !config.articleId) || !post) return null;
      const textOnly = !!postStyle && !hasImage(post.cover);
      const chosenBack = safeArticleLink(config.link);
      const copy = (
        <>
          {config.showBackLink !== false && (
            <a
              className="a-back"
              href={
                chosenBack ??
                (articleContext?.newStyle
                  ? articleContext.backPath
                  : undefined) ??
                articleContext?.detailsPageSlug ??
                settings.detailsPageSlug
              }
            >
              ←{" "}
              {config.linkLabel ||
                (articleContext?.newStyle
                  ? chosenBack && chosenBack !== articleContext.backPath
                    ? "Alle artikler"
                    : overviewPath && postBlog?.title?.trim()
                      ? `Alle ${postBlog.title.trim().toLocaleLowerCase("da-DK")}`
                      : overviewPath
                        ? "Alle artikler"
                        : "Til forsiden"
                  : "Alle fortællinger")}
            </a>
          )}
          {postStyle ? (
            postStyle.eyebrow && (
              <p className="a-eyebrow">
                {topicHref ? (
                  <a href={topicHref}>{postStyle.eyebrow}</a>
                ) : (
                  postStyle.eyebrow
                )}
              </p>
            )
          ) : (
            <p className="a-eyebrow">{post.category}</p>
          )}
          <h1
            className={
              variant === "cover" &&
              (config.headerTitleStyle?.fontRole === "body" ||
                config.headerTitleStyle?.fontRole === "heading")
                ? undefined
                : "primary-font"
            }
          >
            {post.title}
          </h1>
          {config.showExcerpt !== false &&
            (postStyle ? (
              !!post.excerpt?.trim() && (
                <p className="a-post-excerpt">{post.excerpt}</p>
              )
            ) : (
              <p className="a-post-excerpt">{post.excerpt}</p>
            ))}
          {config.showMetadata !== false && (
            <Metadata
              post={post}
              authors={authors}
              locale={config.dateLocale}
              readingLabel={config.readingLabel}
              showDate={config.showDate}
              showAuthor={config.showAuthor}
              variant="author"
              authorHref={authorHref}
            />
          )}
        </>
      );
      content = (
        <div
          className={`a-post-header a-post-header-${variant}${textOnly ? " a-post-header-text" : ""}`}
          style={variant === "cover" ? articleHeaderStyle(config) : undefined}
        >
          {variant === "cover" && !textOnly && (
            <div className="a-header-image">
              <Photo image={post.cover} priority />
            </div>
          )}
          <div className="a-post-copy">{copy}</div>
          {variant !== "cover" && !textOnly && (
            <figure>
              <Photo image={post.cover} priority />
              {post.cover?.caption && (
                <figcaption>{post.cover.caption}</figcaption>
              )}
            </figure>
          )}
        </div>
      );
      break;
    }
    case "metadata":
      content =
        (articleContext || config.articleId) && post ? (
          <Metadata
            post={post}
            authors={authors}
            locale={config.dateLocale}
            readingLabel={config.readingLabel}
            showDate={config.showDate}
            showAuthor={config.showAuthor}
            variant={variant}
          />
        ) : null;
      break;
    case "body": {
      if ((!articleContext && !config.articleId) || !post) return null;
      const headings = post.body.flatMap((block) =>
        block.type === "richText" || block.type === "imageText"
          ? [
              ...block.html.matchAll(
                block.type === "imageText"
                  ? /<h([23456])[^>]*>([\s\S]*?)<\/h\1>/gi
                  : /<h([234])[^>]*>([\s\S]*?)<\/h\1>/gi,
              ),
            ].map((match, i) => ({
              id: `${instanceId}-${block.id}-heading-${i}`,
              title: match[2].replace(/<[^>]*>/g, ""),
            }))
          : [],
      );
      const toc = (
        <nav aria-label="I denne artikel">
          <p className="a-eyebrow">I DENNE ARTIKEL</p>
          {headings.map((item) => (
            <a key={item.id} href={`#${item.id}`}>
              {item.title}
            </a>
          ))}
        </nav>
      );
      content = (
        <div
          className={`a-body a-body-${variant}`}
          style={articleLayoutStyle("bodyPadding", config.bodyPadding)}
        >
          {variant === "toc" && (
            <>
              <aside className="a-desktop-toc">{toc}</aside>
              <details className="a-mobile-toc">
                <summary>I denne artikel</summary>
                {toc}
              </details>
            </>
          )}
          <div className="a-reading">
            {post.body.map((block) => (
              <Block
                key={block.id}
                block={block}
                prefix={instanceId}
                newStyle={!!postStyle}
              />
            ))}
          </div>
        </div>
      );
      break;
    }
    case "media": {
      const bodyImages =
        post?.body.flatMap((block) =>
          block.type === "image" || block.type === "imageText"
            ? [block.image]
            : block.type === "gallery"
              ? block.images
              : [],
        ) ?? [];
      const articleVideo = post?.body.find((block) => block.type === "video");
      const configuredImage = config.image?.url ? [config.image] : undefined;
      const images =
        variant === "gallery"
          ? config.images?.length
            ? config.images
            : bodyImages
          : variant === "video"
            ? (configuredImage ??
              (articleVideo?.poster ? [articleVideo.poster] : []))
            : (configuredImage ??
              (config.images?.length ? config.images : bodyImages));
      const video = config.videoUrl || articleVideo?.url;
      content = (
        <Media
          images={images.length ? images : post?.cover ? [post.cover] : []}
          variant={variant}
          videoUrl={video}
          caption={config.text}
        />
      );
      break;
    }
    case "author": {
      if (!articleContext && !config.articleId) return null;
      if (articleContext?.newStyle && !authors.length) return null;
      content = (
        <div className={`a-authors a-authors-${variant}`}>
          {authors.map((author) => (
            <div key={author.id} className="a-author">
              {author.image &&
              (!articleContext?.newStyle || hasImage(author.image)) ? (
                <div className="a-author-photo">
                  <Photo image={author.image} />
                </div>
              ) : (
                <span className="a-avatar">{author.name.slice(0, 1)}</span>
              )}
              {articleContext?.newStyle ? (
                <div>
                  {variant === "contact" && (
                    <p className="a-eyebrow">Har du et spørgsmål?</p>
                  )}
                  <h2 className="primary-font">
                    {authorHref ? (
                      <a href={authorHref(author)}>{author.name}</a>
                    ) : (
                      author.name
                    )}
                  </h2>
                  {(author.title?.trim() || author.phone?.trim()) && (
                    <p className="a-author-role">
                      {author.title?.trim()}
                      {author.title?.trim() && author.phone?.trim() && " · "}
                      {author.phone?.trim() && (
                        <a
                          href={safeArticleLink(
                            `tel:${author.phone.replace(/\s/g, "")}`,
                          )}
                        >
                          {author.phone.trim()}
                        </a>
                      )}
                    </p>
                  )}
                  {author.bio?.trim() && <p>{author.bio}</p>}
                  {variant === "contact" &&
                    (safeArticleLink(author.url) || author.email?.trim()) && (
                      <div className="a-author-links">
                        {safeArticleLink(author.url) && (
                          <a className="a-button" href={author.url}>
                            Kontakt os
                            {/^\s*https?:\/\//i.test(author.url ?? "") && " ↗"}
                          </a>
                        )}
                        {author.email?.trim() && (
                          <a href={safeArticleLink(`mailto:${author.email}`)}>
                            {author.email}
                          </a>
                        )}
                      </div>
                    )}
                </div>
              ) : (
                <div>
                  <p className="a-eyebrow">
                    {variant === "contact"
                      ? "HAR DU ET SPØRGSMÅL?"
                      : "SKREVET AF"}
                  </p>
                  <h2 className="primary-font">{author.name}</h2>
                  <p>{author.bio}</p>
                  {variant === "contact" && (
                    <div className="a-author-links">
                      {safeArticleLink(author.url) && (
                        <a className="a-button" href={author.url}>
                          Kontakt os ↗
                        </a>
                      )}
                      {author.email && (
                        <a href={safeArticleLink(`mailto:${author.email}`)}>
                          {author.email}
                        </a>
                      )}
                      {author.phone && (
                        <a
                          href={safeArticleLink(
                            `tel:${author.phone.replace(/\s/g, "")}`,
                          )}
                        >
                          {author.phone}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      );
      break;
    }
    case "cta":
      content = (
        <div className={`a-cta a-cta-${variant}`}>
          {variant === "photo" && <Photo image={config.image ?? post?.cover} />}
          <div>
            <p className="a-eyebrow">{config.eyebrow ?? "DIT NÆSTE KAPITEL"}</p>
            <h2 className="primary-font">
              {config.title ??
                (variant === "newsletter"
                  ? "En god fortælling. En gang imellem."
                  : "Skal vi tale om det, du drømmer om?")}
            </h2>
            <p>
              {config.text ?? "Et særligt hjem begynder med en god samtale."}
            </p>
            {safeArticleLink(config.link) && (
              <a className="a-button" href={config.link}>
                {config.linkLabel ??
                  (variant === "newsletter"
                    ? "Tilmeld nyheder"
                    : "Tag en uforpligtende samtale")}{" "}
                ↗
              </a>
            )}
          </div>
          {form && <div className="a-newsletter-form">{form}</div>}
        </div>
      );
      break;
    case "states":
      content =
        variant === "no-image" ? (
          <div className="a-highlights">
            {all
              .slice(0, 2)
              .map((article) => card({ ...article, cover: undefined }))}
          </div>
        ) : (
          <State variant={variant} reset={reset} />
        );
      break;
    default:
      return null;
  }
  return (
    <div
      className="re-articles"
      data-article-family={family}
      data-article-variant={variant}
    >
      {family === "featured" &&
        (config.title || config.eyebrow || config.text) &&
        heading}
      {content}
    </div>
  );
}
